import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Clock3, Gauge } from 'lucide-react';
import { useMyPilotRating, useRatingPilotContexts } from '@tark/shared/hooks';
import {
  ratingPilotEvidenceLabel,
  ratingPilotHeadline,
  ratingPilotIntervalLabel,
  ratingPilotSurfaceState,
} from '@tark/shared/lib';

const PILOT_UI_ENABLED = import.meta.env.VITE_RATING_PILOT_UI_ENABLED === 'true';

export function PilotRatingSummary() {
  const navigate = useNavigate();
  const contextsQuery = useRatingPilotContexts(PILOT_UI_ENABLED);
  const context = contextsQuery.data?.[0];
  const ratingQuery = useMyPilotRating(PILOT_UI_ENABLED && Boolean(context), context?.squadId);
  const rating = ratingQuery.data;
  const headline = rating ? ratingPilotHeadline(rating) : null;
  const evidence = rating ? ratingPilotEvidenceLabel(rating.evidenceState) : null;
  const interval = rating ? ratingPilotIntervalLabel(rating) : null;
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  const surfaceState = ratingPilotSurfaceState({
    loading: contextsQuery.isLoading || ratingQuery.isLoading,
    rating,
    error: contextsQuery.error ?? ratingQuery.error,
    offline: !online,
  });
  const staleLabel = surfaceState === 'stale_offline'
    ? 'Saved rating - offline'
    : surfaceState === 'stale_failed'
      ? 'Saved rating - refresh failed'
      : null;
  const evidenceSummary = rating
    ? [
      rating.officialMatchCount == null ? null : `${rating.officialMatchCount} matches`,
      rating.distinctOpponentCount == null ? null : `${rating.distinctOpponentCount} opponents`,
    ].filter(Boolean).join(' | ')
    : '';

  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);

  if (!PILOT_UI_ENABLED) {
    return null;
  }

  if (surfaceState === 'loading') {
    return (
      <div
        className="flex min-h-20 w-full items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3"
        role="status"
        aria-live="polite"
        aria-label="Loading TARK rating"
        data-testid="pilot-rating-summary-loading"
      >
        <span className="h-11 w-11 shrink-0 rounded-full bg-surface-elevated motion-safe:animate-pulse" />
        <span className="text-sm font-semibold text-text-subtle-readable">Loading TARK rating</span>
      </div>
    );
  }

  const requestFailed = Boolean(contextsQuery.error ?? ratingQuery.error);
  if ((!rating?.available || !headline || !evidence) && requestFailed) {
    const offline = !online;
    return (
      <button
        type="button"
        onClick={() => navigate('/play/rating')}
        data-testid={offline ? 'pilot-rating-summary-offline' : 'pilot-rating-summary-failed'}
        className="flex min-h-20 w-full items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-surface-elevated"
        aria-label={offline ? 'TARK Rating. Offline. Open to retry.' : 'TARK Rating could not refresh. Open to retry.'}
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          {offline ? <Clock3 className="h-5 w-5" aria-hidden="true" /> : <Gauge className="h-5 w-5" aria-hidden="true" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-text-muted">TARK Rating</span>
          <span className="block font-bold text-text">{offline ? 'Offline' : "Couldn't refresh"}</span>
          <span className="block text-xs font-semibold text-text-subtle-readable">Open to try again</span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-text-subtle-readable" aria-hidden="true" />
      </button>
    );
  }

  if (!context) return null;

  if (!rating?.available || !headline || !evidence) {
    return (
      <button
        type="button"
        onClick={() => navigate('/play/rating')}
        data-testid="pilot-rating-summary-building"
        className="flex min-h-20 w-full items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-surface-elevated"
        aria-label="TARK Rating. Building your rating."
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Clock3 className="h-5 w-5" aria-hidden="true" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-text-muted">TARK Rating</span>
          <span className="block font-bold text-text">Building your rating</span>
          <span className="block truncate text-xs font-semibold text-text-subtle-readable">{context.squadName}</span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-text-subtle-readable" aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/play/rating')}
      data-testid="pilot-rating-summary"
      className="flex min-h-20 w-full items-center gap-3 rounded-lg border border-border bg-surface px-4 py-3 text-left transition-colors hover:border-primary/40 hover:bg-surface-elevated"
      aria-label={`TARK rating ${headline}. ${evidence}.${interval ? ` 80 percent range ${interval}.` : ''}`}
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Gauge className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-xs font-semibold text-text-muted">TARK Rating</span>
        <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2">
          <span className="text-2xl font-bold tabular-nums text-text">{headline}</span>
          <span className="text-sm font-semibold text-text-secondary">{evidence}</span>
        </span>
        {interval ? <span className="text-xs font-semibold text-text-subtle-readable">80% range {interval}</span> : null}
        {evidenceSummary ? <span className="block text-xs font-semibold text-text-subtle-readable">{evidenceSummary}</span> : null}
        {staleLabel ? <span className="block text-xs font-bold text-text-subtle-readable" aria-live="polite">{staleLabel}</span> : null}
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-text-subtle-readable" aria-hidden="true" />
    </button>
  );
}
